import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiStorefront, PiPaintRoller, PiArrowRightBold } from "react-icons/pi";

interface CtaCard {
  title: string;
  subtitle: string;
  buttonText: string;
  icon: string;
  url?: string;
}

export default function HokCta({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#684DF4";
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const defaultCards: CtaCard[] = [
    {
      title: "Want to Teach?",
      subtitle: "Become an Instructor",
      buttonText: "Start Teaching",
      icon: "store",
    },
    {
      title: "Looking to Learn?",
      subtitle: "Browse Premium Courses",
      buttonText: "Explore Now",
      icon: "theme",
    },
  ];

  const cards: CtaCard[] = content.cards || defaultCards;

  const updateCard = (idx: number, field: keyof CtaCard, value: string) => {
    if (!onContentChange) return;
    const newCards = [...cards];
    newCards[idx] = { ...newCards[idx], [field]: value };
    onContentChange({ ...content, cards: newCards });
  };

  const addCard = () => {
    if (!onContentChange) return;
    const newCards = [...cards, {
      title: "New CTA",
      subtitle: "Description here",
      buttonText: "Learn More",
      icon: "store",
      url: "#",
    }];
    onContentChange({ ...content, cards: newCards });
  };

  const removeCard = (idx: number) => {
    if (!onContentChange) return;
    const newCards = cards.filter((_, i) => i !== idx);
    onContentChange({ ...content, cards: newCards });
  };

  return (
    <section
      className={`relative overflow-hidden w-full py-10 sm:py-14 ${
        isSelected ? "ring-2 ring-blue-500 relative z-20" : ""
      }`}
      style={{ fontFamily }}
    >
      {/* Background with gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${primaryColor}08 0%, ${secondaryColor}08 100%)`,
        }}
      />

      {/* Decorative circles */}
      <div
        className="absolute -top-20 -left-20 w-80 h-80 rounded-full opacity-20 blur-3xl"
        style={{ background: primaryColor }}
      />
      <div
        className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full opacity-20 blur-3xl"
        style={{ background: secondaryColor }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {cards.map((card, idx) => (
            <div
              key={idx}
              className="group relative overflow-hidden rounded-3xl p-6 sm:p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
              style={{
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
              }}
            >
              {isSelected && cards.length > 1 && (
                <button
                  onClick={() => removeCard(idx)}
                  className="absolute top-4 right-4 h-8 w-8 rounded-full bg-white/20 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm font-bold hover:bg-white/30"
                  title="Remove Card"
                >
                  ×
                </button>
              )}

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6">
                  {card.icon === "store" ? (
                    <PiStorefront className="w-7 h-7 text-white" />
                  ) : (
                    <PiPaintRoller className="w-7 h-7 text-white" />
                  )}
                </div>

                {/* Title */}
                <h3 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-2">
                  <EditableText
                    value={card.title}
                    onChange={(val) => updateCard(idx, "title", val)}
                    as="span"
                    placeholder="CTA Title"
                  />
                </h3>

                {/* Subtitle */}
                <p className="text-white/80 text-base sm:text-lg mb-6">
                  <EditableText
                    value={card.subtitle}
                    onChange={(val) => updateCard(idx, "subtitle", val)}
                    as="span"
                    placeholder="CTA Subtitle"
                  />
                </p>

                {/* Button */}
                <a
                  href={card.url || "#"}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white font-bold text-sm transition-all hover:gap-3 hover:shadow-lg"
                  style={{ color: primaryColor }}
                >
                  <EditableText
                    value={card.buttonText}
                    onChange={(val) => updateCard(idx, "buttonText", val)}
                    as="span"
                    placeholder="Button Text"
                  />
                  <PiArrowRightBold className="w-4 h-4" />
                </a>
              </div>

              {/* Decorative shape */}
              <div
                className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 transform translate-x-10 -translate-y-10"
                style={{ background: "white" }}
              />
            </div>
          ))}

          {isSelected && cards.length < 3 && (
            <button
              onClick={addCard}
              className="flex flex-col items-center justify-center min-h-[200px] rounded-3xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all"
            >
              <span className="text-4xl mb-2">+</span>
              <span className="text-sm font-semibold">Add CTA Card</span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
